import React, { useState } from "react";
import { KeyRound, ShieldCheck, LogOut, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Stage-4c · Account & Security
// Self-serve password change for the currently signed-in user (owner OR
// partner). Backend enforces:
//   * current_password must verify against the stored bcrypt hash
//   * new_password >= 12 chars and must differ from current
//   * new_password === confirm_password
//   * password_changed_at is set to now → invalidates every previously
//     issued JWT for THIS account (including the one making the change)
// After success we always drop the local token and route to /login so the
// user must sign in again with the new password.
export default function AccountSecurity() {
  const { email, role, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const validationError = (() => {
    if (!current) return "Enter your current password.";
    if (next.length < 12) return "New password must be at least 12 characters.";
    if (next === current) return "New password must differ from the current password.";
    if (next !== confirm) return "New password and confirmation don't match.";
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      // Server has invalidated our JWT; wipe local state and force fresh login.
      toast.success("Password updated. Please sign in again with the new password.");
      setCurrent("");
      setNext("");
      setConfirm("");
      // Small delay so the toast is visible, then hard-logout.
      setTimeout(() => {
        logout();
        window.location.assign("/login?rotated=1");
      }, 900);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Password change failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950" data-testid="account-security-page">
      <PageHeader
        eyebrow="Module 09 · Account & Security"
        title="Change my password"
        description="Rotate the password on YOUR account. Other team members cannot rotate your credentials from this screen — every request runs against the signed-in identity. After a successful change, all active sessions for this account are signed out and you'll be asked to sign in again."
        testid="account-security-header"
      />
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6 space-y-6">
        <div className="border border-zinc-950 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
            <div className="label-xs">Currently signed in</div>
          </div>
          <div className="font-mono text-sm text-zinc-800">
            {email} · <span className="uppercase text-[10px] tracking-wider text-zinc-500">{role}</span>
          </div>
        </div>

        <form onSubmit={submit} className="border border-zinc-950 p-5 space-y-4" data-testid="change-password-form" autoComplete="off">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4" aria-hidden="true" />
            <div className="label-xs">Change password</div>
          </div>

          <div>
            <Label htmlFor="cp-current" className="text-xs font-bold">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="rounded-none border-zinc-950 h-10 mt-1 font-mono"
              placeholder="Your current password"
              data-testid="cp-current-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="cp-new" className="text-xs font-bold">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="rounded-none border-zinc-950 h-10 mt-1 font-mono"
              placeholder="At least 12 characters"
              minLength={12}
              data-testid="cp-new-input"
              required
            />
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              Minimum 12 characters. Choose something you don't reuse anywhere else.
            </div>
          </div>

          <div>
            <Label htmlFor="cp-confirm" className="text-xs font-bold">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-none border-zinc-950 h-10 mt-1 font-mono"
              placeholder="Retype the new password"
              minLength={12}
              data-testid="cp-confirm-input"
              required
            />
          </div>

          {validationError && (current || next || confirm) && (
            <div
              className="flex items-start gap-2 bg-amber-50 border border-amber-300 p-2 text-xs text-amber-900"
              role="alert"
              data-testid="cp-validation-msg"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{validationError}</span>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button
              type="submit"
              disabled={saving || !!validationError}
              className="rounded-sm h-10 bg-zinc-950 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider px-5 disabled:opacity-40"
              data-testid="cp-submit-button"
            >
              {saving ? "Saving…" : "Update password & sign out"}
            </Button>
            <div className="text-[10px] font-mono text-zinc-500">
              You'll be redirected to sign in again after this call.
            </div>
          </div>
        </form>

        <div className="border border-dashed border-zinc-300 p-4 text-[11px] font-mono text-zinc-500 leading-relaxed">
          <div className="flex items-center gap-2 mb-2 text-zinc-700">
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" /> Session invalidation contract
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>The new password is hashed with the existing bcrypt setup — nothing about the storage changes.</li>
            <li>Every JWT issued before the change (including the one this browser is using) is rejected immediately.</li>
            <li>Team members cannot rotate this password from any other screen — the endpoint only operates on the caller's own account.</li>
            <li>If you're locked out of an owner account, the seed script `ADMIN_EMAIL` / `ADMIN_PASSWORD` env vars can be reset out-of-band by an operator with pod access.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
